import React, { useState } from 'react';
import { motion } from 'framer-motion';
import { MapPin, Mail, Linkedin, Send, CheckCircle2 } from 'lucide-react';
import { toast } from 'sonner';
import axios from 'axios';

const API = process.env.REACT_APP_BACKEND_URL;

const fadeUp = {
  hidden: { opacity: 0, y: 24 },
  visible: (i = 0) => ({ opacity: 1, y: 0, transition: { duration: 0.6, delay: i * 0.1, ease: 'easeOut' } }),
};

const socialLinks = [
  { icon: Linkedin, href: 'https://www.linkedin.com/company/biovex-technologies/', label: 'LinkedIn' },
];

export default function ContactPage() {
  const [form, setForm] = useState({ name: '', email: '', message: '' });
  const [loading, setLoading] = useState(false);
  const [submitted, setSubmitted] = useState(false);

  const handleChange = (e) => setForm({ ...form, [e.target.name]: e.target.value });

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!form.name || !form.email || !form.message) {
      toast.error('Please fill in all fields.');
      return;
    }
    setLoading(true);
    try {
      await axios.post(`${API}/api/contact`, form);
      setSubmitted(true);
      toast.success('Message sent! We\'ll be in touch soon.');
    } catch {
      toast.error('Something went wrong. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  return (
    <main className="pt-24">
      {/* ── Page Hero ── */}
      <section className="px-6 md:px-12 lg:px-24 py-20 md:py-28 relative overflow-hidden">
        <div
          className="absolute inset-0 pointer-events-none"
          style={{ background: 'radial-gradient(ellipse 60% 50% at 20% 40%, rgba(0,240,255,0.08) 0%, transparent 60%)' }}
        />
        <div className="max-w-7xl relative z-10">
          <motion.div variants={fadeUp} initial="hidden" animate="visible" custom={0}>
            <span className="section-label">Contact Us</span>
          </motion.div>
          <motion.h1
            variants={fadeUp} initial="hidden" animate="visible" custom={1}
            className="font-outfit font-bold text-5xl sm:text-6xl text-white mt-6 mb-6 leading-tight"
            data-testid="contact-heading"
          >
            Let's Build<br />
            <span className="text-gradient-cyan">Something Together</span>
          </motion.h1>
          <motion.p
            variants={fadeUp} initial="hidden" animate="visible" custom={2}
            className="text-white/55 font-inter-tight text-lg max-w-xl leading-relaxed"
          >
            Whether you're an investor, researcher, or potential partner — we'd love to hear from you.
          </motion.p>
        </div>
      </section>

      {/* ── Contact Section ── */}
      <section className="px-6 md:px-12 lg:px-24 pb-24 border-t border-bvx-border">
        <div className="max-w-7xl pt-16 grid grid-cols-1 lg:grid-cols-5 gap-12">
          {/* Form — 3 cols */}
          <motion.div
            variants={fadeUp} initial="hidden" whileInView="visible" viewport={{ once: true }}
            className="lg:col-span-3"
          >
            {submitted ? (
              <div className="glass-card p-10 flex flex-col items-start gap-5" data-testid="success-message">
                <div className="w-14 h-14 rounded-2xl bg-bvx-green/15 border border-bvx-green/30 flex items-center justify-center">
                  <CheckCircle2 size={26} className="text-bvx-green" />
                </div>
                <h2 className="font-outfit font-bold text-white text-2xl">Message Received</h2>
                <p className="text-white/55 font-inter-tight text-sm leading-relaxed max-w-sm">
                  Thank you for reaching out. A member of the Biovex team will review your message and respond within 2–3 business days.
                </p>
                <button
                  onClick={() => { setSubmitted(false); setForm({ name: '', email: '', message: '' }); }}
                  className="btn-outline text-sm mt-2"
                  data-testid="send-another-btn"
                >
                  Send Another Message
                </button>
              </div>
            ) : (
              <div className="glass-card p-8">
                <h2 className="font-outfit font-bold text-white text-xl mb-6">Send a Message</h2>
                <form onSubmit={handleSubmit} className="space-y-5" data-testid="contact-form">
                  <div>
                    <label className="block font-rajdhani font-semibold uppercase tracking-widest text-white/40 text-xs mb-2">
                      Full Name
                    </label>
                    <input
                      type="text"
                      name="name"
                      value={form.name}
                      onChange={handleChange}
                      placeholder="Your full name"
                      data-testid="contact-name-input"
                      className="w-full h-12 px-4 rounded-lg bg-white/5 border border-white/10 focus:border-bvx-cyan/50 focus:outline-none text-white text-sm font-inter-tight placeholder:text-white/25 transition-colors duration-200"
                    />
                  </div>
                  <div>
                    <label className="block font-rajdhani font-semibold uppercase tracking-widest text-white/40 text-xs mb-2">
                      Email Address
                    </label>
                    <input
                      type="email"
                      name="email"
                      value={form.email}
                      onChange={handleChange}
                      placeholder="you@example.com"
                      data-testid="contact-email-input"
                      className="w-full h-12 px-4 rounded-lg bg-white/5 border border-white/10 focus:border-bvx-cyan/50 focus:outline-none text-white text-sm font-inter-tight placeholder:text-white/25 transition-colors duration-200"
                    />
                  </div>
                  <div>
                    <label className="block font-rajdhani font-semibold uppercase tracking-widest text-white/40 text-xs mb-2">
                      Message
                    </label>
                    <textarea
                      name="message"
                      value={form.message}
                      onChange={handleChange}
                      placeholder="Tell us about your project, investment interest, or how you'd like to collaborate..."
                      rows={6}
                      data-testid="contact-message-input"
                      className="w-full px-4 py-3 rounded-lg bg-white/5 border border-white/10 focus:border-bvx-cyan/50 focus:outline-none text-white text-sm font-inter-tight placeholder:text-white/25 transition-colors duration-200 resize-none"
                    />
                  </div>
                  <button
                    type="submit"
                    disabled={loading}
                    className="btn-primary flex items-center gap-2 disabled:opacity-50 disabled:cursor-not-allowed w-full justify-center"
                    data-testid="contact-submit-btn"
                  >
                    {loading ? 'Sending...' : (
                      <><Send size={14} /> Send Message</>
                    )}
                  </button>
                </form>
              </div>
            )}
          </motion.div>

          {/* Info — 2 cols */}
          <motion.div
            variants={fadeUp} initial="hidden" whileInView="visible" custom={0.2} viewport={{ once: true }}
            className="lg:col-span-2 flex flex-col gap-5"
          >
            <div className="glass-card p-6">
              <h3 className="font-outfit font-semibold text-white text-base mb-4">Contact Details</h3>
              <div className="space-y-3">
                <div className="flex items-center gap-3 text-white/55 text-sm font-inter-tight">
                  <Mail size={15} className="text-bvx-cyan shrink-0" />
                  <span>goswamishreyansh83@gmail.com</span>
                </div>
                <div className="flex items-start gap-3 text-white/55 text-sm font-inter-tight">
                  <MapPin size={15} className="text-bvx-cyan shrink-0 mt-0.5" />
                  <span>India</span>
                </div>
              </div>
            </div>

            <div className="glass-card p-6">
              <h3 className="font-outfit font-semibold text-white text-base mb-4">Follow Our Work</h3>
              <div className="space-y-3">
                {socialLinks.map(({ icon: Icon, href, label }) => (
                  <a
                    key={label}
                    href={href}
                    target="_blank"
                    rel="noopener noreferrer"
                    data-testid={`contact-social-${label.toLowerCase().split('/')[0].trim()}`}
                    className="flex items-center gap-3 text-white/55 hover:text-bvx-cyan text-sm font-inter-tight transition-colors duration-200"
                  >
                    <Icon size={15} className="shrink-0" />
                    {label}
                  </a>
                ))}
              </div>
            </div>

            <div className="glass-card p-6">
              <h3 className="font-outfit font-semibold text-white text-base mb-3">Who We Talk To</h3>
              <ul className="space-y-2">
                {['Investors & VCs', 'Research Collaborators', 'Healthcare Institutions', 'Technology Partners', 'Press & Media'].map((item) => (
                  <li key={item} className="flex items-center gap-2 text-white/50 text-sm font-inter-tight">
                    <div className="w-1 h-1 rounded-full bg-bvx-cyan" />
                    {item}
                  </li>
                ))}
              </ul>
            </div>
          </motion.div>
        </div>
      </section>
    </main>
  );
}
